import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { ArrowLeft, Plus, Gift, Trash2, Edit, Share2, Copy, CheckCircle, Package, List, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import CreateRewardModal from '@/components/CreateRewardModal';
import ShareModal from '@/components/ShareModal';
import ConfirmModal from '@/components/ConfirmModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RewardsView() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [list, setList] = useState(null);
  const [taskList, setTaskList] = useState(null); // Associated task list
  const [rewards, setRewards] = useState([]);
  const [user, setUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeTab, setActiveTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  useEffect(() => {
    fetchData();
  }, [listId]);

  const fetchData = async () => {
    try {
      const [userRes, listRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/reward-lists/${listId}`, { withCredentials: true }),
        axios.get(`${API}/rewards/${listId}`, { withCredentials: true })
      ]);
      
      // Verify user data is valid
      if (!userRes.data || !userRes.data.user_id) {
        console.log('No valid user session, redirecting to login');
        navigate('/login');
        return;
      }
      
      setUser(userRes.data);
      setList(listRes.data);
      setRewards(rewardsRes.data);
      
      // Load associated task list if available
      if (listRes.data.task_list_id) {
        try {
          const taskListRes = await axios.get(`${API}/task-lists/${listRes.data.task_list_id}`, { withCredentials: true });
          setTaskList(taskListRes.data);
        } catch (error) {
          console.error('Error loading associated task list:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // If authentication error, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403 || !error.response) {
        console.log('Authentication error, redirecting to login');
        navigate('/login');
        return;
      }
      
      toast.error(t('messages.errorLoading'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateListName = async () => {
    if (!editedName.trim() || editedName === list.name) {
      setIsEditingName(false);
      return;
    }
    try {
      const response = await axios.put(`${API}/reward-lists/${listId}`, { name: editedName.trim() }, { withCredentials: true });
      setList(response.data);
      setIsEditingName(false);
      toast.success(t('messages.listRenamed'));
    } catch (error) {
      console.error('Error updating list name:', error);
      if (error.response?.status === 403) {
        toast.error(t('messages.onlyOwnerCanRename'));
      } else {
        toast.error(t('messages.errorUpdating'));
      }
    }
  };

  const getLocale = () => {
    const localeMap = { es: 'es-ES', en: 'en-US', gl: 'gl-ES', ca: 'ca-ES', zh: 'zh-CN' };
    return localeMap[i18n.language] || 'es-ES';
  };

  // Get available points from associated task list (user's points, not total)
  const availablePoints = taskList?.user_points ?? taskList?.points ?? 0;

  const handleRedeemReward = async (rewardId, costPoints, rewardTitle) => {
    if (availablePoints < costPoints) {
      toast.error(t('messages.notEnoughPoints'));
      return;
    }

    setConfirmAction({
      type: 'redeem',
      title: t('modals.confirmRedeem'),
      message: t('modals.confirmRedeemMessage', { title: rewardTitle, points: costPoints }),
      action: async () => {
        try {
          await axios.post(`${API}/rewards/${rewardId}/redeem`, {}, { withCredentials: true });
          await fetchData();
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 }
          });
          toast.success(t('messages.rewardRedeemed'));
        } catch (error) {
          console.error('Error redeeming reward:', error);
          const errorMessage = error.response?.data?.detail || t('messages.errorRedeemingReward');
          toast.error(errorMessage);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteReward = async (rewardId) => {
    setConfirmAction({
      type: 'delete',
      title: t('modals.deleteReward'),
      message: t('modals.deleteRewardMessage'),
      action: async () => {
        try {
          await axios.delete(`${API}/rewards/${rewardId}`, { withCredentials: true });
          fetchData();
          toast.success(t('messages.rewardDeleted'));
        } catch (error) {
          console.error('Error deleting reward:', error);
          toast.error(t('messages.errorDeletingReward'));
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleDeleteList = async () => {
    setConfirmAction({
      type: 'deleteList',
      title: t('modals.deleteList'),
      message: t('modals.deleteRewardListMessage'),
      action: async () => {
        try {
          await axios.delete(`${API}/reward-lists/${listId}`, { withCredentials: true });
          toast.success(t('messages.listDeleted'));
          navigate('/');
        } catch (error) {
          console.error('Error deleting list:', error);
          toast.error(t('messages.errorDeletingList'));
        }
      }
    });
    setShowConfirmModal(true);
  };

  const confirmModalAction = async () => {
    if (confirmAction && confirmAction.action) {
      await confirmAction.action();
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const handleCloneReward = async (rewardId) => {
    try {
      await axios.post(`${API}/rewards/${rewardId}/clone`, {}, { withCredentials: true });
      fetchData();
      toast.success(t('messages.rewardCloned'));
    } catch (error) {
      console.error('Error cloning reward:', error);
      toast.error(t('messages.errorCloningReward'));
    }
  };

  const handleMarkDelivered = async (rewardId, rewardTitle) => {
    setConfirmAction({
      type: 'markDelivered',
      title: t('modals.confirmDelivery'),
      message: t('modals.confirmDeliveryMessage', { title: rewardTitle }),
      action: async () => {
        try {
          await axios.post(`${API}/rewards/${rewardId}/mark-delivered`, {}, { withCredentials: true });
          await fetchData();
          toast.success(t('messages.rewardDelivered'));
        } catch (error) {
          console.error('Error marking as delivered:', error);
          const errorMessage = error.response?.data?.detail || t('messages.errorDeliveringReward');
          toast.error(errorMessage);
        }
      }
    });
    setShowConfirmModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#18181B] border-t-transparent"></div>
      </div>
    );
  }

  const isOwner = list?.owner_id === user?.user_id;

  const availableRewards = rewards.filter(r => r.status === 'available');
  const pendingRewards = rewards.filter(r => r.status === 'pending_delivery');
  const deliveredRewards = rewards.filter(r => r.status === 'delivered');

  const currentRewards = activeTab === 'available' ? availableRewards :
                         activeTab === 'pending' ? pendingRewards :
                         deliveredRewards;

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6" data-testid="rewards-view">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate('/')}
            data-testid="back-btn"
            className="mb-3 sm:mb-4 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base font-bold text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all flex items-center gap-1.5 sm:gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('tasks.back')}
          </button>

          <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl sm:rounded-2xl hard-shadow p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  {isEditingName ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateListName();
                          if (e.key === 'Escape') setIsEditingName(false);
                        }}
                        autoFocus
                        className="flex-1 text-xl sm:text-2xl md:text-3xl font-bold bg-transparent border-b-2 border-[#18181B] dark:border-zinc-400 focus:outline-none focus:border-[#ADE498] text-zinc-900 dark:text-white"
                        data-testid="edit-reward-list-name-input"
                      />
                      <button
                        onClick={handleUpdateListName}
                        className="p-1.5 rounded-lg bg-[#ADE498] hover:bg-[#9dd889] border-2 border-[#18181B]"
                        data-testid="save-reward-list-name-btn"
                      >
                        <Check className="w-4 h-4 text-[#18181B]" />
                      </button>
                      <button
                        onClick={() => setIsEditingName(false)}
                        className="p-1.5 rounded-lg bg-zinc-200 dark:bg-zinc-600 hover:bg-zinc-300 dark:hover:bg-zinc-500 border-2 border-[#18181B] dark:border-zinc-500"
                      >
                        <X className="w-4 h-4 text-zinc-900 dark:text-white" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-zinc-900 dark:text-white" data-testid="list-name">{list?.name}</h1>
                      {isOwner && (
                        <button
                          onClick={() => {
                            setEditedName(list?.name || '');
                            setIsEditingName(true);
                          }}
                          className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                          title={t('rewards.renameList')}
                          data-testid="edit-reward-list-name-btn"
                        >
                          <Pencil className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
                  {isOwner ? t('tasks.yourList') : t('tasks.sharedList')} • {rewards.length} {t('rewards.rewards')}
                </p>
              </div>
              <div className="flex gap-2">
                {isOwner && (
                  <>
                    <button
                      onClick={() => setShowShareModal(true)}
                      data-testid="share-list-btn"
                      className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 p-1.5 sm:p-2 hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all"
                    >
                      <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-900 dark:text-white" />
                    </button>
                    <button
                      onClick={handleDeleteList}
                      data-testid="delete-list-btn"
                      className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 p-1.5 sm:p-2 hover:bg-red-50 dark:hover:bg-red-900/30 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] transition-all"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* User Points Display - Now shows task list points */}
            <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-[#FFC247]/20 dark:bg-[#FFC247]/10 border-2 border-[#18181B] dark:border-zinc-600 rounded-lg sm:rounded-xl">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFC247] border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-[#18181B]" />
              </div>
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">{t('rewards.availablePoints')}</p>
                <p className="text-xl sm:text-2xl font-mono font-bold text-zinc-900 dark:text-white" data-testid="user-points">{availablePoints}</p>
              </div>
              {taskList && (
                <div className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 bg-white/50 dark:bg-zinc-700/50 px-2 py-1 rounded-full">
                  <List className="w-3 h-3" />
                  <span>{taskList.name}</span>
                </div>
              )}
            </div>

            {list?.shared_with?.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm mt-3 sm:mt-4">
                <span className="text-zinc-500 dark:text-zinc-400">{t('tasks.sharedWith')}</span>
                <div className="flex flex-wrap gap-1">
                  {list.shared_with.map((email, i) => (
                    <span key={i} className="px-2 py-0.5 sm:py-1 bg-zinc-100 dark:bg-zinc-700 rounded-md text-xs truncate max-w-[150px] text-zinc-700 dark:text-zinc-300">
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Reward Button */}
        <button
          onClick={() => {
            setEditingReward(null);
            setShowCreateModal(true);
          }}
          data-testid="add-reward-btn"
          className="w-full mb-4 sm:mb-6 rounded-full border-2 border-[#18181B] bg-[#ADE498] px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-bold hover:bg-[#bdeaa8] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
          {t('rewards.addReward')}
        </button>

        {/* Tabs */}
        <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:overflow-visible sm:flex-wrap">
          <button
            onClick={() => setActiveTab('available')}
            data-testid="tab-available"
            className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'available'
                ? 'bg-[#18181B] text-white shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]'
                : 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700'
            }`}
          >
            <Gift className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">{t('rewards.available')}</span> ({availableRewards.length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending"
            className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'pending'
                ? 'bg-[#18181B] text-white shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]'
                : 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700'
            }`}
          >
            <Package className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">{t('rewards.pendingDelivery')}</span> ({pendingRewards.length})
          </button>
          <button
            onClick={() => setActiveTab('delivered')}
            data-testid="tab-delivered"
            className={`rounded-full border-2 border-[#18181B] dark:border-zinc-600 px-3 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-bold transition-all flex items-center gap-1.5 sm:gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'delivered'
                ? 'bg-[#18181B] text-white shadow-[2px_2px_0px_0px_rgba(24,24,27,1)]'
                : 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700'
            }`}
          >
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">{t('rewards.history')}</span> ({deliveredRewards.length})
          </button>
        </div>

        {/* Rewards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {currentRewards.map((reward) => {
            const canAfford = availablePoints >= reward.cost_points;
            const redemptions = Array.isArray(reward.redeemed_by) ? reward.redeemed_by : [];
            const userHasRedeemed = redemptions.some(r => r && r.user_id === user?.user_id);

            return (
              <div
                key={reward.reward_id}
                data-testid={`reward-${reward.reward_id}`}
                className={`bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl sm:rounded-2xl hard-shadow p-4 sm:p-6 transition-all ${
                  activeTab === 'delivered' ? 'opacity-75' : ''
                }`}
              >
                {/* Reward Icon */}
                <div className="mb-3 sm:mb-4">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-[#18181B] dark:border-zinc-600 flex items-center justify-center ${
                    activeTab === 'available' ? 'bg-[#ADE498]' :
                    activeTab === 'pending' ? 'bg-[#FFC247]' :
                    'bg-zinc-200 dark:bg-zinc-600'
                  }`}>
                    {activeTab === 'available' && <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-[#18181B]" />}
                    {activeTab === 'pending' && <Package className="w-6 h-6 sm:w-8 sm:h-8 text-[#18181B]" />}
                    {activeTab === 'delivered' && <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-[#18181B] dark:text-white" />}
                  </div>
                </div>

                {/* Reward Content */}
                <h3 className="text-base sm:text-xl font-bold mb-2 sm:mb-3 break-words text-zinc-900 dark:text-white">{reward.title}</h3>
                <div className="mb-3 sm:mb-4">
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-[#FFC247] border-2 border-[#18181B] dark:border-zinc-600 rounded-lg font-mono font-bold text-sm sm:text-lg inline-block text-[#18181B]">
                    {reward.cost_points} pts
                  </span>
                </div>

                {/* Redemption info */}
                {redemptions.length > 0 && (
                  <div className="mb-3 sm:mb-4 space-y-1.5 sm:space-y-2">
                    <div className="text-[10px] sm:text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase">{t('rewards.redeemedBy')}</div>
                    {redemptions.map((redemption, idx) => (
                      <div key={idx} className="px-2 sm:px-3 py-1.5 sm:py-2 bg-[#ADE498]/30 dark:bg-[#ADE498]/20 border border-[#18181B] dark:border-zinc-600 rounded-lg text-xs sm:text-sm">
                        <div className="font-bold truncate text-zinc-900 dark:text-white">{redemption.user_name}</div>
                        <div className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400">
                          {new Date(redemption.redeemed_at).toLocaleDateString(getLocale(), { 
                            day: 'numeric', 
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Delivered info */}
                {activeTab === 'delivered' && reward.delivered_at && (
                  <div className="mb-3 sm:mb-4 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-100 dark:bg-zinc-700 border border-[#18181B] dark:border-zinc-600 rounded-lg text-xs sm:text-sm">
                    <div className="text-[10px] sm:text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase mb-0.5 sm:mb-1">{t('rewards.delivered')}</div>
                    <div className="text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-300">
                      {new Date(reward.delivered_at).toLocaleDateString(getLocale(), { 
                        day: 'numeric', 
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                  {/* Available tab - Show redeem button */}
                  {activeTab === 'available' && !userHasRedeemed && (
                    <button
                      onClick={() => handleRedeemReward(reward.reward_id, reward.cost_points, reward.title)}
                      disabled={!canAfford}
                      data-testid={`redeem-reward-${reward.reward_id}`}
                      className={`flex-1 rounded-full border-2 border-[#18181B] font-bold py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm transition-all ${
                        canAfford
                          ? 'bg-[#ADE498] hover:bg-[#bdeaa8] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none'
                          : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                      }`}
                    >
                      {t('rewards.redeem')}
                    </button>
                  )}
                  
                  {/* Pending tab - Show mark as delivered button */}
                  {activeTab === 'pending' && (
                    <button
                      onClick={() => handleMarkDelivered(reward.reward_id, reward.title)}
                      data-testid={`mark-delivered-${reward.reward_id}`}
                      className="flex-1 rounded-full border-2 border-[#18181B] bg-[#ADE498] font-bold py-1.5 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm hover:bg-[#bdeaa8] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-1 sm:gap-2"
                    >
                      <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">{t('rewards.markAsDelivered')}</span> {t('rewards.markDelivered')}
                    </button>
                  )}

                  {/* Delivered tab - Show clone button */}
                  {activeTab === 'delivered' && (
                    <button
                      onClick={() => handleCloneReward(reward.reward_id)}
                      data-testid={`clone-reward-${reward.reward_id}`}
                      className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-1.5 sm:py-2 px-3 sm:px-4 text-xs sm:text-sm hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all flex items-center justify-center gap-1"
                    >
                      <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {t('rewards.clone')}
                    </button>
                  )}

                  {/* Edit and Delete available in all tabs */}
                  {activeTab !== 'delivered' && (
                    <>
                      <button
                        onClick={() => {
                          setEditingReward(reward);
                          setShowCreateModal(true);
                        }}
                        data-testid={`edit-reward-${reward.reward_id}`}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                      >
                        <Edit className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-900 dark:text-white" />
                      </button>
                      <button
                        onClick={() => handleDeleteReward(reward.reward_id)}
                        data-testid={`delete-reward-${reward.reward_id}`}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {currentRewards.length === 0 && (
            <div className="col-span-full text-center py-8 sm:py-12 text-zinc-400">
              {activeTab === 'available' && (
                <>
                  <Gift className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">{t('rewards.noAvailable')}</p>
                </>
              )}
              {activeTab === 'pending' && (
                <>
                  <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">{t('rewards.noPending')}</p>
                </>
              )}
              {activeTab === 'delivered' && (
                <>
                  <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">{t('rewards.noDelivered')}</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateRewardModal
          listId={listId}
          reward={editingReward}
          onClose={() => {
            setShowCreateModal(false);
            setEditingReward(null);
          }}
          onCreated={() => {
            fetchData();
            setShowCreateModal(false);
            setEditingReward(null);
          }}
        />
      )}

      {showShareModal && (
        <ShareModal
          listId={listId}
          type="reward"
          onClose={() => setShowShareModal(false)}
          onShared={() => {
            fetchData();
            setShowShareModal(false);
          }}
        />
      )}

      {showConfirmModal && confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          onConfirm={confirmModalAction}
          onCancel={() => {
            setShowConfirmModal(false);
            setConfirmAction(null);
          }}
        />
      )}
    </div>
  );
}
